import {
  AdjustmentsHorizontalIcon,
  BellIcon,
  BookOpenIcon,
  Cog6ToothIcon,
  CpuChipIcon,
  DocumentDuplicateIcon,
  DocumentPlusIcon,
  UserGroupIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { UserRole } from "../models/user.model";

export interface OperationsPanelTab {
  id: string;
  label: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
  path: string;
  requiredRoles: UserRole[];
  isEnabled?: boolean;
  badge?: string;
}

export const OPERATIONS_TABS: OperationsPanelTab[] = [
  {
    id: "cv-management",
    label: "CV Management",
    icon: DocumentPlusIcon,
    path: "cv-management",
    requiredRoles: [UserRole.Admin, UserRole.Manager], // Key change: Managers can access
    isEnabled: true,
  },
  {
    id: "users",
    label: "User Management",
    icon: UsersIcon,
    path: "users",
    requiredRoles: [UserRole.Admin], // Admin-only
    isEnabled: true,
  },
  {
    id: "dictionaries",
    label: "Dictionaries",
    icon: BookOpenIcon,
    path: "dictionaries",
    requiredRoles: [UserRole.Admin, UserRole.Manager], // Both can access business data
    isEnabled: true,
  },
  {
    id: "data",
    label: "Data Management",
    icon: Cog6ToothIcon,
    path: "data",
    requiredRoles: [UserRole.Admin], // Admin-only (technical system administration)
    isEnabled: false, // CV indexing moved to AI Management; kept for backward-compat
  },
  {
    id: "employee-hub",
    label: "Employee",
    icon: UserGroupIcon,
    path: "employee-hub",
    requiredRoles: [UserRole.Admin],
    isEnabled: true,
  },
  {
    id: "notification-testing",
    label: "Notifications",
    icon: BellIcon,
    path: "notification-testing",
    requiredRoles: [UserRole.Admin], // Admin-only
    isEnabled: true,
  },
  {
    id: "ai-management",
    label: "AI Management",
    icon: CpuChipIcon,
    path: "ai-management",
    requiredRoles: [UserRole.Admin], // Admin-only
    isEnabled: true,
  },
  {
    id: "profile-completion-config",
    label: "Profile Completion Config",
    icon: AdjustmentsHorizontalIcon,
    path: "profile-completion-config",
    requiredRoles: [UserRole.Admin, UserRole.Manager],
    isEnabled: true,
  },
  // Future tabs (currently disabled/commented in AdminNav)
  {
    id: "teams",
    label: "Teams",
    icon: UserGroupIcon,
    path: "teams",
    requiredRoles: [UserRole.Admin, UserRole.Manager],
    isEnabled: false, // Currently disabled
  },
  {
    id: "templates",
    label: "Templates",
    icon: DocumentDuplicateIcon,
    path: "templates",
    requiredRoles: [UserRole.Admin, UserRole.Manager],
    isEnabled: false, // Currently disabled
  },
  {
    id: "filters",
    label: "Filters",
    icon: AdjustmentsHorizontalIcon,
    path: "filters",
    requiredRoles: [UserRole.Admin],
    isEnabled: false, // Currently disabled
  },
];

// Helper function to get visible tabs for a specific role
export const getVisibleTabsForRole = (
  userRole: UserRole
): OperationsPanelTab[] => {
  return OPERATIONS_TABS.filter(
    (tab) => tab.isEnabled && tab.requiredRoles.includes(userRole)
  );
};

// Helper function to check if user has access to specific tab
export const hasAccessToTab = (tabId: string, userRole: UserRole): boolean => {
  const tab = OPERATIONS_TABS.find((t) => t.id === tabId);
  return tab ? tab.requiredRoles.includes(userRole) : false;
};
